import { useState } from "react"
import type { LayerDefinition, LayerFilter } from "@/types/explore"

interface LayerItemProps {
  layer: LayerDefinition
  active: boolean
  onToggle: () => void
  isQuick?: number
  onToggleQuick?: (id: string) => void
  onFilterChange?: (filterId: string, value: unknown) => void
  dynamicOptions?: Record<string, string[]>
}

const SliderFilter = ({ label, onChange }: { label: string; onChange?: (value: number) => void }) => {
  const [val, setVal] = useState(80)
  return (
    <label className="flex items-center gap-2 text-sm text-gray-500">
      <span className="w-14 text-gray-400">{label}</span>
      <input
        type="range"
        min="0"
        max="100"
        value={val}
        onChange={(e) => {
          const v = Number(e.target.value)
          setVal(v)
          onChange?.(v)
        }}
        className="opacity-track flex-1"
        style={{
          background: `linear-gradient(to right, #1E40AF ${val}%, #E5E7EB ${val}%)`,
        }}
      />
      <span className="w-8 text-right text-xs tabular-nums text-gray-400">{val}%</span>
    </label>
  )
}

const MultiFilter = ({
  label,
  options,
  onChange,
  colors,
  labels,
}: {
  label: string
  options: string[]
  onChange?: (selected: string[]) => void
  colors?: Record<string, string>
  labels?: Record<string, string>
}) => {
  const [selected, setSelected] = useState<string[]>([])
  const toggle = (o: string) => {
    const next = selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]
    setSelected(next)
    onChange?.(next)
  }
  return (
    <div className="text-sm">
      <span className="text-gray-400 text-xs">{label}</span>
      <div className="flex flex-wrap gap-1 mt-1">
        {options.map((o) => {
          const color = colors?.[o]
          const isSelected = selected.includes(o)
          return (
            <button
              key={o}
              onClick={() => toggle(o)}
              className={`transition-all duration-[120ms] cursor-pointer select-none active:scale-95 px-2 py-0.5 rounded-lg text-xs font-medium ${
                !color
                  ? isSelected
                    ? "bg-primary text-white shadow-sm"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  : isSelected
                    ? "ring-2 ring-gray-800 ring-offset-1"
                    : "opacity-35 hover:opacity-60"
              }`}
              style={
                color
                  ? {
                      backgroundColor: color,
                      color: getContrastColor(color),
                    }
                  : undefined
              }
            >
              {labels?.[o] ?? o}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const getContrastColor = (rgb: string): string => {
  const match = rgb.match(/\d+/g)
  if (!match) return "#000"
  const [r, g, b] = match.map(Number)
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#1a1a2e" : "#fff"
}

const NumberFilter = ({ label, onChange }: { label: string; onChange?: (value: number | undefined) => void }) => {
  const [val, setVal] = useState("")
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-20 text-gray-400 text-xs">{label}</span>
      <input
        type="number"
        value={val}
        onChange={(e) => {
          setVal(e.target.value)
          onChange?.(e.target.value ? Number(e.target.value) : undefined)
        }}
        className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs w-16 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/10"
      />
    </label>
  )
}

const TextFilter = ({ label, onChange }: { label: string; onChange?: (value: string | undefined) => void }) => {
  const [val, setVal] = useState("")
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-20 text-gray-400 text-xs">{label}</span>
      <input
        type="text"
        value={val}
        onChange={(e) => {
          setVal(e.target.value)
          onChange?.(e.target.value || undefined)
        }}
        className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs w-16 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/10"
      />
    </label>
  )
}

const SelectFilter = ({
  label,
  options,
  onChange,
}: {
  label: string
  options: string[]
  onChange?: (value: string) => void
}) => {
  const [val, setVal] = useState(options[0] || "")
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-20 text-gray-400 text-xs">{label}</span>
      <select
        value={val}
        onChange={(e) => {
          setVal(e.target.value)
          onChange?.(e.target.value)
        }}
        className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/10 bg-white"
      >
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </label>
  )
}

export const FilterControl = ({
  filter,
  onChange,
  dynamicOptions,
}: {
  filter: LayerFilter
  onChange?: (value: unknown) => void
  dynamicOptions?: Record<string, string[]>
}) => {
  const options = filter.dynamic && dynamicOptions?.[filter.id] ? dynamicOptions[filter.id] : filter.options || []
  switch (filter.type) {
    case "slider":
      return <SliderFilter label={filter.label} onChange={onChange} />
    case "number":
      return <NumberFilter label={filter.label} onChange={onChange} />
    case "text":
      return <TextFilter label={filter.label} onChange={onChange} />
    case "select":
      return <SelectFilter label={filter.label} options={options} onChange={onChange} />
    case "multi":
      if (filter.dynamic && options.length === 0) {
        return (
          <div className="text-sm">
            <span className="text-gray-400 text-xs">{filter.label}</span>
            <p className="text-xs text-gray-400 italic mt-1">Selectionnez une zone d'abord</p>
          </div>
        )
      }
      return (
        <MultiFilter
          label={filter.label}
          options={options}
          onChange={onChange}
          colors={filter.colors}
          labels={filter.labels}
        />
      )
    default:
      return null
  }
}

export const InlineOpacity = ({ onChange }: { onChange?: (value: number) => void }) => {
  const [val, setVal] = useState(80)
  return (
    <div className="flex items-center gap-1.5 ml-auto shrink-0">
      <input
        type="range"
        min="0"
        max="100"
        value={val}
        onChange={(e) => {
          const v = Number(e.target.value)
          setVal(v)
          onChange?.(v)
        }}
        className="opacity-track w-14"
        style={{
          background: `linear-gradient(to right, #1E40AF ${val}%, #E5E7EB ${val}%)`,
        }}
      />
      <span className="text-xs tabular-nums text-gray-400 w-6 text-right">{val}%</span>
    </div>
  )
}

const FilterSection = ({
  filters,
  onFilterChange,
  dynamicOptions,
}: {
  filters: LayerFilter[]
  onFilterChange?: (filterId: string, value: unknown) => void
  dynamicOptions?: Record<string, string[]>
}) => {
  const [open, setOpen] = useState(true)
  return (
    <div className="anim-fade-in">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 ml-7 mt-1 text-xs text-gray-400 hover:text-gray-500 cursor-pointer select-none transition-colors"
      >
        <svg
          className={`w-3 h-3 transition-transform duration-150 ${open ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span>Filtres ({filters.length})</span>
      </button>
      {open && (
        <div className="ml-7 pl-3 border-l-2 border-primary/10 space-y-2 py-1.5 mb-0.5 anim-fade-in">
          {filters.map((f) => (
            <FilterControl
              key={f.id}
              filter={f}
              onChange={(v) => onFilterChange?.(f.id, v)}
              dynamicOptions={dynamicOptions}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const LayerItem = ({
  layer,
  active,
  onToggle,
  isQuick,
  onToggleQuick,
  onFilterChange,
  dynamicOptions,
}: LayerItemProps) => {
  const opacityFilter = layer.filters?.find((f) => f.type === "slider")
  const otherFilters = layer.filters?.filter((f) => f.type !== "slider") || []

  return (
    <div>
      <div className="flex items-center gap-1 group">
        <button
          onClick={onToggle}
          className={`transition-all duration-150 cursor-pointer select-none active:scale-[0.97] flex-1 flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-left ${
            active
              ? "bg-primary/[0.08] border border-primary/20"
              : "bg-gray-50/60 border border-transparent hover:bg-gray-100/80"
          }`}
        >
          <div
            className={`w-2 h-2 rounded-full shrink-0 transition-all duration-150 ${active ? "scale-125" : "scale-100"}`}
            style={{ backgroundColor: active ? "#1E40AF" : "#CBD5E1" }}
          />
          <span
            className={`text-sm flex-1 transition-colors duration-100 ${active ? "text-primary font-medium" : "text-gray-500"}`}
          >
            {layer.name}
          </span>
          {active && opacityFilter && (
            <div onClick={(e) => e.stopPropagation()}>
              <InlineOpacity onChange={(v) => onFilterChange?.(opacityFilter.id, v)} />
            </div>
          )}
        </button>
        {onToggleQuick && (
          <button
            data-tour-step="layer-quick-star"
            onClick={(e) => {
              e.stopPropagation()
              onToggleQuick(layer.id)
            }}
            className={`cursor-pointer transition-all duration-150 hover:scale-[1.15] active:scale-95 shrink-0 p-1 ${
              isQuick ? "text-warning anim-pop" : "text-gray-300 opacity-0 group-hover:opacity-100 hover:text-warning"
            }`}
            title={isQuick ? "Retirer du panneau rapide" : "Ajouter au panneau rapide"}
          >
            {isQuick ? (
              <span className="relative inline-flex items-center justify-center w-5 h-5">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>
                <span className="absolute font-bold text-[9px] top-[5px] text-amber-600">{isQuick}</span>
              </span>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      {active && otherFilters.length > 0 && (
        <FilterSection filters={otherFilters} onFilterChange={onFilterChange} dynamicOptions={dynamicOptions} />
      )}
    </div>
  )
}
